import React, { useEffect, useState, useCallback, useMemo } from 'react';  
import styles from '../../../Routes/Routes.module.css';  
import layout from '../../../Modules/layout.module.css';  
import { AreaDisplayInfo } from '../../../Types/AreaDisplayInfo/AreaDisplayInfo';  
import { ToggleTemplateDisabledStatus, ToggleQuestionnaireTemplateArchivedById, GetMandatedTemplatesDisplayInfoByClient, GetMesmaTemplatesDisplayInfoByClient, UpdateQuestionnaireTemplateTitle } from '../../../API/TemplateBank/TemplateBank';  
import PagedCardCollection from '../LandingPages/Evaluate/PagedCardCollection';  
import SearchBar from '../../SearchBar/SearchBar';  
import CreationBar from '../Actions/CreationBar/CreationBar';
import { ITab } from '../../../Types/ITab';
import TabbedView from '../TabbedView/TabbedView';
import { useLocation, useNavigate } from 'react-router-dom';
import {useAppSelector} from "../../../Redux/Store/Store";
import {getClient, getDevelopEnabled} from "../../../Redux/Store/Slices/client";
import { toast } from 'react-toastify';
import { ProductType } from '../../../Types/ProductType';
import { axiosInstance } from '../../../Authentication/AxiosInterceptor';
import Button from '../Button/Button';
import DropDownList from '../DropDownList/DropDownList';
import popupStyles from './TemplateManagment.module.css'
import { IDropDownListItem } from '../../../Types/DropDownList/IDropDownListItem';
import CompactFilters, { ISelectOption } from '../../Analyse/CompactFilters/CompactFilters';

type TemplateManagementProps = {
  tabs: ITab[];
  showCreateButton?: boolean;
  onCreateTemplate?: (title: string, templateType: string) => void;
  createButtonPlaceholder?: string;
  createButtonNoTextMessage?: string;
  showTitleEdit?: boolean;
  mesmaOnly?: boolean; // New prop to determine if we should only show Mesma templates
};

const TemplateManagement = ({ 
  tabs, 
  onCreateTemplate,
  createButtonPlaceholder = 'Enter title for new template',
  createButtonNoTextMessage = 'The Title field is required',
  showTitleEdit = false,
  mesmaOnly = false
}: TemplateManagementProps) => {  
    const { pathname } = useLocation();
    const client = useAppSelector(getClient);
    const developEnabled = useAppSelector(getDevelopEnabled);
    const isArchive = pathname.endsWith('/archive');
    const activeTab = isArchive ? tabs[1] : tabs[0];
    const [displayInfos, setDisplayInfos] = useState<AreaDisplayInfo[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [pageCount, setPageCount] = useState<number>(0);
    const [activePage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const pageSize = 6;
    const [showCreateTemplatePopup, setShowCreateTemplatePopup] = useState<boolean>(false);
    const [selectedTemplateType, setSelectedTemplateType] = useState<IDropDownListItem | null>(null);
    const [selectedTemplateTypes, setSelectedTemplateTypes] = useState<ISelectOption[]>([]);

    const [typeOptions, setTypeOptions] = useState<IDropDownListItem[]>([
        { value: 'Evaluate', text: 'Evaluate', colour: '#24394F', textColour: '#FFFFFF' },
    ]);

    // Template type filter options for CompactFilters
    const templateTypeFilterOptions: ISelectOption[] = useMemo(() => {
        const base: ISelectOption[] = [
            { label: 'Evaluate', value: ProductType.Evaluate.toString(), required: false },
            { label: 'Track', value: ProductType.Improve.toString(), required: false },
        ];
        if (developEnabled === true) {
            base.push({ label: 'Develop', value: ProductType.Develop.toString(), required: false });
        }
        return base;
    }, [developEnabled]);

    useEffect(() => {
        const evaluateOption: IDropDownListItem = { value: 'Evaluate', text: 'Evaluate', colour: '#24394F', textColour: '#FFFFFF' };
        const trackOption: IDropDownListItem = { value: 'Improve', text: 'Track', colour: '#52BFA2', textColour: '#FFFFFF' };
        const developOption: IDropDownListItem = { value: 'Develop', text: 'Develop', colour: '#4F46E5', textColour: '#FFFFFF' };
        const showDevelop = developEnabled === true;
        setTypeOptions(showDevelop ? [evaluateOption, trackOption, developOption] : [evaluateOption, trackOption]);
        setSelectedTemplateType((prev) => (prev?.value === 'Develop' && !showDevelop ? null : prev));
    }, [developEnabled]);

    useEffect(() => {
        if (developEnabled !== true) {
            setSelectedTemplateTypes((prev) => prev.filter((o) => o.value !== ProductType.Develop.toString()));
        }
    }, [developEnabled]);

    const navigate = useNavigate();

    const fetchData = useCallback(async () => {   
        setLoading(true);

        if (client) {
            // Choose the appropriate endpoint based on mesmaOnly prop
            const fetchFunction = mesmaOnly 
                ? GetMesmaTemplatesDisplayInfoByClient 
                : GetMandatedTemplatesDisplayInfoByClient;
            
            fetchFunction(client.id, isArchive)
                .then(templates => {
                    // Don't filter by search term here - let PagedCardCollection handle it
                    setDisplayInfos(templates);
                    setPageCount(Math.ceil(templates.length / pageSize));
                    setLoading(false);
                })
                .catch(error => {
                    console.error(`Error fetching ${mesmaOnly ? 'Mesma' : 'mandated'} templates:`, error);
                    setLoading(false);
                });
        }
    }, [isArchive, activePage, client, mesmaOnly, showTitleEdit]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearchTermChange = (value: string) => {  
        setSearchTerm(value);  
    };

    const toggleDisabledHandler = async (id: string) => {
        if (!client) return;
        await ToggleTemplateDisabledStatus(id, client.id);
        fetchData();
    };

    const toggleArchiveHandler = async (id: string) => {
        if (!client) return;
        
        try {
            await ToggleQuestionnaireTemplateArchivedById(id, client.id);
            
            // Find the template to get its title for the toast
            const template = displayInfos.find(t => t.id === id);
            const templateTitle = template?.title || 'Template';
            
            // Show success toast based on current tab
            if (isArchive) {
                toast.success(`${templateTitle} has been unarchived successfully.`);
            } else {
                toast.success(`${templateTitle} has been archived successfully.`);
            }
            
            fetchData();
        } catch (error) {
            console.error('Error toggling archive status:', error);
            toast.error('Failed to update archive status. Please try again.');
        }
    };

    const titleChangedHandler = async (templateId: string, title: string) => {
        if (!client) return;
        await UpdateQuestionnaireTemplateTitle(templateId, title, client.id);
        fetchData();
    };

    const productTypeChanged = async (type: ProductType, templateId: string) => {
        if (!client) return;
        const response = await axiosInstance.post(`ChangeTemplateProductType/${templateId}/${client.id}/${type}`);
        if (response.status === 200) {
            if(response.data as ProductType == type){
                toast.success('Diagnostic type updated.');
            }else{
                toast.error('Diagnostic type update failed.');
            }
        }
        fetchData();
    };

    const toggleCreateTemplatePopup = () => {
        if(mesmaOnly){
            setShowCreateTemplatePopup(!showCreateTemplatePopup);
            setSelectedTemplateType(null);
        }
        else{
            navigate('/admin/create-a-template')
        }

    };

    const handleTypeChange = (type: IDropDownListItem) => {
        setSelectedTemplateType(type);
    };

    const handleCreateTemplate = (title: string) => {
        if (!selectedTemplateType) {
            toast.warning('Please select a template type before creating.');
            return;
        }
        if (onCreateTemplate) {
            onCreateTemplate(title, selectedTemplateType.value);
        }
        setShowCreateTemplatePopup(false);
        setSelectedTemplateType(null);
    };

    const filterConfig = useMemo(() => [
        { key: 'templateTypes', label: 'Type', placeholder: 'Select template types', dataSource: 'props' as const, data: templateTypeFilterOptions },
    ], [templateTypeFilterOptions]);

    const selectedFilters = useMemo(() => ({
        templateTypes: selectedTemplateTypes,
    }), [selectedTemplateTypes]);

    const handleFilterChange = (filters: Record<string, ISelectOption[]>) => {
        setSelectedTemplateTypes(filters.templateTypes || []);
    };


    return (  
        <div className={styles.container}>
            <div className={styles.body}>  
                <div className={layout.column2}>
                    <div className={layout.rowSpan}>
                        <SearchBar value={searchTerm} onChange={handleSearchTermChange} type="" />  
                        <TabbedView data={tabs} activeTab={activeTab} activeTabChanged={() => {}}/>
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem'}}>
                        <div style={{display: 'flex', gap: '1rem', alignItems: 'center', width: '100%'}}>
                            <div style={{flex: 4}}>
                                <CompactFilters
                                    filters={filterConfig}
                                    selectedFilters={selectedFilters}
                                    onFilterChange={handleFilterChange}
                                />
                            </div>
                            <div style={{flexShrink: 0}}>
                                <Button clickHandler={toggleCreateTemplatePopup} style={{maxWidth: '175px'}}>
                                    Create Template
                                </Button>
                            </div>
                        </div>
                    </div>

                    {showCreateTemplatePopup && onCreateTemplate && (
                        <div className={popupStyles.createTemplatePopup}>
                            <div className={popupStyles.createTemplatePopup__field}>
                                <label>Select a template type</label>
                                <DropDownList value={selectedTemplateType?.value || ''} onChange={handleTypeChange} placeholder="Choose template type" data={typeOptions} />
                            </div>
                            <CreationBar 
                                onCreateHandler={handleCreateTemplate} 
                                placeholder={createButtonPlaceholder} 
                                noTextMessage={createButtonNoTextMessage} 
                            />
                        </div>
                    )}

                    <PagedCardCollection 
                        productTypeChanged={productTypeChanged}
                        toggleArchiveHandler={toggleArchiveHandler}
                        toggleDisabledHandler={toggleDisabledHandler}
                        template={true}
                        displayInfos={displayInfos}
                        archived={isArchive} 
                        pageChanged={setPageCount} 
                        activePage={activePage} 
                        pageCount={pageCount} 
                        loading={loading} 
                        titleChangedHandler={showTitleEdit ? titleChangedHandler : undefined}
                        showToggleSlider={true}
                        selectedTemplateTypes={selectedTemplateTypes}
                        searchTerm={searchTerm}
                    />
                </div>  
            </div>  
        </div>
    );
};

export default TemplateManagement;
